import {
  type AdminCompany,
  type AdminCompanyCreated,
  type AdminCompanyInput,
  type AdminUser,
  type AdminUserInput,
  APP_MODULES,
  adminCompanyInput,
  adminUserInput,
  COMPANY_PLANS,
  type CompanyPlan,
  identificationTypeLabel,
  type Paginated,
  type SystemStatus,
} from '@facture/contracts'
import { zodResolver } from '@hookform/resolvers/zod'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import {
  ActivityIcon,
  Building2Icon,
  CheckIcon,
  CopyIcon,
  DatabaseIcon,
  KeyRoundIcon,
  LifeBuoyIcon,
  MoreHorizontalIcon,
  PlusIcon,
  PowerIcon,
  Settings2Icon,
  ShieldIcon,
  UsersIcon,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { useAppActions } from '@/components/app/use-app-actions'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { confirm } from '@/components/ui/confirm'
import { columnHelper, DataTable, useDebounced } from '@/components/ui/data-table'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Field } from '@/components/ui/field'
import { ClearFilters, FilterBar, FilterSearch } from '@/components/ui/filter-bar'
import { Input } from '@/components/ui/input'
import { Avatar, Switch } from '@/components/ui/misc'
import { Page, PageHeader } from '@/components/ui/page'
import { Select } from '@/components/ui/select'
import {
  AccessPicker,
  type AccessValue,
  EMPTY_ACCESS,
  hasAccess,
  toAccessInput,
} from '@/features/admin/access-pickers'
import { CompanyMembersSheet, UserAccessSheet } from '@/features/admin/access-sheets'
import { AnnouncementsPanel } from '@/features/admin/announcements-panel'
import { ApiUsagePanel } from '@/features/admin/api-usage-panel'
import { AuditPanel } from '@/features/admin/audit-panel'
import { CabysCard } from '@/features/admin/cabys-card'
import { CertificatesCard } from '@/features/admin/certificates-card'
import { HolidaysPanel } from '@/features/admin/holidays-panel'
import { LegacyPanel } from '@/features/admin/legacy-panel'
import { SendingSwitch } from '@/features/admin/sending-switch'
import { CompanyModulesDialog, ModuleList } from '@/features/modules/company-modules-dialog'
import { moduleMeta } from '@/features/modules/module-meta'
import { api, errorMessage } from '@/lib/api'
import { copyText } from '@/lib/clipboard'
import { formatDate, formatIdentification, formatRelative } from '@/lib/format'
import { useSession } from '@/lib/session'

const searchSchema = z.object({
  tab: z
    .enum(['system', 'companies', 'users', 'announcements', 'legacy', 'audit', 'api-usage', 'holidays'])
    .optional(),
  q: z.string().optional(),
  page: z.number().int().min(1).optional(),
})

export const Route = createFileRoute('/_app/admin')({
  staticData: { crumb: 'Administración' },
  validateSearch: searchSchema,
  beforeLoad: () => {
    if (!useSession.getState().user?.isSuperadmin) throw redirect({ to: '/' })
  },
  component: AdminPage,
})

const SECTION = {
  system: {
    title: 'Sistema',
    description: 'Salud de la plataforma, envío de comprobantes y llaves por vencer.',
  },
  companies: { title: 'Compañías', description: 'Compañías de la plataforma, su plan y su estado.' },
  users: { title: 'Usuarios', description: 'Usuarios de toda la plataforma y sus compañías.' },
  announcements: { title: 'Anuncios', description: 'Avisos que ven todos los usuarios al entrar.' },
  legacy: { title: 'Migración', description: 'Traé compañías y datos desde el sistema anterior.' },
  audit: {
    title: 'Bitácora',
    description: 'Quién hizo qué en la plataforma: accesos, cambios y configuraciones.',
  },
  'api-usage': {
    title: 'Uso de API',
    description: 'Llamadas, errores y comprobantes emitidos por la API de integración, por compañía y mes.',
  },
  holidays: {
    title: 'Feriados',
    description:
      'Feriados de Costa Rica para la planilla de todas las compañías (Art. 148 del Código de Trabajo).',
  },
} as const

function AdminPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const tab = search.tab ?? 'system'
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  return (
    <Page>
      <PageHeader
        eyebrow={
          <span className="flex items-center gap-1.5">
            <ShieldIcon className="size-3.5" /> Plataforma
          </span>
        }
        title={SECTION[tab].title}
        description={SECTION[tab].description}
      />
      {tab === 'system' && <SystemPanel />}
      {tab === 'companies' && <CompaniesPanel search={search} onSearch={setSearch} />}
      {tab === 'users' && <UsersPanel search={search} onSearch={setSearch} />}
      {tab === 'announcements' && <AnnouncementsPanel />}
      {tab === 'legacy' && <LegacyPanel />}
      {tab === 'audit' && <AuditPanel />}
      {tab === 'api-usage' && <ApiUsagePanel />}
      {tab === 'holidays' && <HolidaysPanel />}
    </Page>
  )
}

function SystemPanel() {
  const { data } = useQuery({
    queryKey: ['admin', 'system'],
    queryFn: () => api.get<SystemStatus>('/admin/system'),
    refetchInterval: 10_000,
  })
  const hours = data ? Math.floor(data.uptimeSeconds / 3600) : 0
  return (
    <div className="grid gap-4">
      <SendingSwitch />
      <CertificatesCard />
      <CabysCard />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label="Base de datos"
          value={data ? (data.database.ok ? 'En línea' : 'Sin conexión') : '…'}
          hint={data ? `${data.database.latencyMs} ms` : ''}
          ok={data?.database.ok}
          icon={DatabaseIcon}
        />
        <Stat
          label="Compañías"
          value={data ? String(data.companies) : '…'}
          hint="Esquemas aprovisionados"
          icon={Building2Icon}
        />
        <Stat
          label="Usuarios"
          value={data ? String(data.users) : '…'}
          hint="En toda la plataforma"
          icon={UsersIcon}
        />
        <Stat
          label="Tiempo en línea"
          value={data ? `${hours} h ${Math.floor((data.uptimeSeconds % 3600) / 60)} min` : '…'}
          hint={`Versión ${data?.version ?? ''}`}
          icon={ActivityIcon}
        />
      </div>
      <Card className="overflow-hidden">
        <div className="border-border border-b px-5 py-4">
          <h3 className="font-semibold">Colas de trabajo</h3>
          <p className="text-muted-foreground text-sm">Envíos a Hacienda, mensajes receptor y correos.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[34rem] text-sm">
            <thead className="bg-subtle text-muted-foreground text-xs">
              <tr>
                {['Cola', 'En espera', 'Activos', 'Programados', 'Fallidos'].map((h, i) => (
                  <th
                    key={h}
                    className={
                      i === 0 ? 'px-5 py-2 text-left font-medium' : 'px-5 py-2 text-right font-medium'
                    }
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.queues.map((q) => (
                <tr key={q.name} className="border-border border-t">
                  <td className="px-5 py-3 font-mono text-xs">{q.name}</td>
                  <td className="px-5 py-3 text-right tabular">{q.waiting}</td>
                  <td className="px-5 py-3 text-right tabular">{q.active}</td>
                  <td className="px-5 py-3 text-right tabular">{q.delayed}</td>
                  <td className="px-5 py-3 text-right tabular">
                    {q.failed > 0 ? <Badge variant="destructive">{q.failed}</Badge> : 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}

function Stat({
  label,
  value,
  hint,
  icon: Icon,
  ok,
}: {
  label: string
  value: string
  hint?: string
  icon: typeof DatabaseIcon
  ok?: boolean
}) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between text-muted-foreground text-sm">
        {label}
        <Icon className="size-4" />
      </div>
      <div className="mt-2 flex items-center gap-2 font-semibold text-2xl tracking-tight">
        {ok !== undefined && (
          <span className={`size-2.5 rounded-full ${ok ? 'bg-success' : 'bg-destructive'}`} />
        )}
        {value}
      </div>
      {hint && <div className="mt-1 text-muted-foreground text-xs">{hint}</div>}
    </Card>
  )
}

type PanelProps = {
  search: z.infer<typeof searchSchema>
  onSearch: (patch: Partial<z.infer<typeof searchSchema>>) => void
}

function useSearchText(search: PanelProps['search'], onSearch: PanelProps['onSearch']) {
  const [text, setText] = useState(search.q ?? '')
  const q = useDebounced(text)
  // biome-ignore lint/correctness/useExhaustiveDependencies: solo reacciona al texto debounced
  useEffect(() => {
    if (q !== (search.q ?? '')) onSearch({ q: q || undefined, page: undefined })
  }, [q])
  return [text, setText] as const
}

const companyCol = columnHelper<AdminCompany>()

function CompaniesPanel({ search, onSearch }: PanelProps) {
  const queryClient = useQueryClient()
  const { enterSupportCompany } = useAppActions()
  const [text, setText] = useSearchText(search, onSearch)
  const [open, setOpen] = useState(false)
  const [membersOf, setMembersOf] = useState<AdminCompany | null>(null)
  const [modulesOf, setModulesOf] = useState<AdminCompany | null>(null)
  const [secret, setSecret] = useState<{ email: string; password: string } | null>(null)
  const query = { search: search.q, page: search.page ?? 1, pageSize: 25 }
  const list = useQuery({
    queryKey: ['admin', 'companies', query],
    queryFn: () => api.get<Paginated<AdminCompany>>('/admin/companies', { query }),
    placeholderData: keepPreviousData,
  })
  const toggleHacienda = useMutation({
    mutationFn: (c: AdminCompany) =>
      api.patch(`/admin/companies/${c.id}/hacienda`, { enabled: !c.haciendaEnabled }),
    onSuccess: () => {
      toast.success('Modo de Hacienda actualizado')
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] })
    },
  })
  const setPlan = useMutation({
    mutationFn: ({ id, plan }: { id: string; plan: CompanyPlan }) =>
      api.patch(`/admin/companies/${id}/plan`, { plan }),
    onSuccess: () => {
      toast.success('Plan actualizado: aplica de inmediato a todos sus usuarios')
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] })
    },
  })
  const toggle = useMutation({
    mutationFn: (c: AdminCompany) =>
      api.patch(`/admin/companies/${c.id}/status`, {
        status: c.status === 'active' ? 'suspended' : 'active',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] }),
  })

  const columns = useMemo(
    () => [
      companyCol.accessor('legalName', {
        header: 'Compañía',
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="font-medium">{row.original.tradeName ?? row.original.legalName}</div>
            <div className="text-muted-foreground text-xs">
              {formatIdentification(row.original.identificationType, row.original.identification)} ·{' '}
              {row.original.email}
            </div>
          </div>
        ),
      }),
      companyCol.accessor('owners', {
        header: 'Propietario',
        meta: { hideOnMobile: true },
        cell: ({ getValue }) => <span className="text-muted-foreground text-sm">{getValue()[0] ?? '—'}</span>,
      }),
      companyCol.accessor('members', { header: 'Miembros', meta: { align: 'right' } }),
      companyCol.accessor('schemaName', {
        header: 'Esquema',
        meta: { hideOnMobile: true },
        cell: ({ getValue }) => <span className="font-mono text-muted-foreground text-xs">{getValue()}</span>,
      }),
      companyCol.accessor('plan', {
        header: 'Plan',
        cell: ({ row }) => (
          <Select
            value={row.original.plan}
            onValueChange={(plan) => setPlan.mutate({ id: row.original.id, plan: plan as CompanyPlan })}
            className="h-8 w-32 text-xs"
            options={COMPANY_PLANS.map((p) => ({ value: p.code, label: p.name }))}
          />
        ),
      }),
      companyCol.accessor('modules', {
        header: 'Módulos',
        cell: ({ row }) => {
          const active = APP_MODULES.filter((m) => !m.core && row.original.modules.includes(m.key))
          return (
            <button
              type="button"
              onClick={() => setModulesOf(row.original)}
              className="group -mx-1.5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-1.5 py-1 transition hover:bg-accent"
              title="Módulos de la compañía"
            >
              {active.length ? (
                active.map((m) => {
                  const { icon: Icon, tone } = moduleMeta(m.key)
                  return (
                    <span
                      key={m.key}
                      className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 font-medium text-xs ${tone}`}
                    >
                      <Icon className="size-3" /> {m.name}
                    </span>
                  )
                })
              ) : (
                <span className="whitespace-nowrap text-muted-foreground text-xs">Solo incluidos</span>
              )}
              <Settings2Icon className="size-3.5 text-muted-foreground opacity-0 transition group-hover:opacity-100" />
            </button>
          )
        },
      }),
      companyCol.accessor('haciendaEnabled', {
        header: 'Hacienda',
        meta: { hideOnMobile: true },
        cell: ({ row }) => (
          <button
            type="button"
            onClick={() => toggleHacienda.mutate(row.original)}
            className="rounded-md transition hover:opacity-80"
            title="Cambiar modo"
          >
            {row.original.haciendaEnabled ? (
              <Badge variant="info">Envía a Hacienda</Badge>
            ) : (
              <Badge>Solo interno</Badge>
            )}
          </button>
        ),
      }),
      companyCol.accessor('status', {
        header: 'Estado',
        cell: ({ getValue }) =>
          getValue() === 'active' ? (
            <Badge variant="success" dot>
              Activa
            </Badge>
          ) : (
            <Badge variant="warning" dot>
              Suspendida
            </Badge>
          ),
      }),
      companyCol.display({
        id: 'actions',
        header: '',
        meta: { align: 'right', className: 'w-32' },
        cell: ({ row }) => (
          <div className="flex justify-end gap-0.5">
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Usuarios de la compañía"
              title="Usuarios de la compañía"
              onClick={() => setMembersOf(row.original)}
            >
              <UsersIcon />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Abrir compañía (modo soporte)"
              title="Abrir compañía (modo soporte)"
              onClick={() => enterSupportCompany(row.original).catch((e) => toast.error(errorMessage(e)))}
            >
              <LifeBuoyIcon />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={row.original.status === 'active' ? 'Suspender' : 'Activar'}
              onClick={() =>
                confirm({
                  title:
                    row.original.status === 'active'
                      ? `¿Suspender ${row.original.legalName}?`
                      : `¿Reactivar ${row.original.legalName}?`,
                  description:
                    row.original.status === 'active'
                      ? 'Sus usuarios no podrán entrar y se detienen los envíos a Hacienda.'
                      : undefined,
                  destructive: row.original.status === 'active',
                  onConfirm: () => toggle.mutateAsync(row.original),
                })
              }
            >
              <PowerIcon />
            </Button>
          </div>
        ),
      }),
    ],
    [toggle.mutateAsync, toggleHacienda.mutate, setPlan.mutate, enterSupportCompany],
  )

  return (
    <>
      <DataTable
        columns={columns}
        data={list.data?.items}
        loading={list.isFetching}
        getRowId={(c) => c.id}
        isRowMuted={(c) => c.status !== 'active'}
        toolbar={
          <FilterBar
            actions={
              <Button onClick={() => setOpen(true)}>
                <PlusIcon /> Nueva compañía
              </Button>
            }
          >
            <FilterSearch value={text} onChange={setText} placeholder="Buscar por nombre o cédula…" />
            <ClearFilters active={!!text} onClear={() => setText('')} />
          </FilterBar>
        }
        pagination={
          list.data && {
            page: list.data.page,
            pageSize: list.data.pageSize,
            total: list.data.total,
            onPageChange: (p) => onSearch({ page: p }),
          }
        }
      />
      <CreateCompanyDialog
        open={open}
        onOpenChange={setOpen}
        onCreated={(r) => {
          queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] })
          if (r.ownerTemporaryPassword)
            setSecret({ email: r.company.owners[0] ?? '', password: r.ownerTemporaryPassword })
        }}
      />
      <CompanyMembersSheet company={membersOf} onClose={() => setMembersOf(null)} />
      <CompanyModulesDialog company={modulesOf} onClose={() => setModulesOf(null)} />
      <SecretDialog secret={secret} onClose={() => setSecret(null)} />
    </>
  )
}

function CreateCompanyDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  onCreated: (r: AdminCompanyCreated) => void
}) {
  const form = useForm<AdminCompanyInput>({
    resolver: zodResolver(adminCompanyInput),
    defaultValues: {
      identificationType: '02',
      legalName: '',
      identification: '',
      email: '',
      ownerEmail: '',
      ownerName: '',
      plan: 'full',
      modules: [],
    },
  })
  useEffect(() => {
    if (open) form.reset()
  }, [open, form])
  const create = useMutation({
    mutationFn: (v: AdminCompanyInput) => api.post<AdminCompanyCreated>('/admin/companies', v),
    onSuccess: (r) => {
      toast.success(`${r.company.legalName} aprovisionada`)
      onCreated(r)
      onOpenChange(false)
    },
  })
  const { errors } = form.formState
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <form onSubmit={form.handleSubmit((v) => create.mutate(v))} noValidate>
          <DialogHeader>
            <DialogTitle>Nueva compañía</DialogTitle>
            <DialogDescription>
              Se crea su esquema de datos, roles, casa matriz y el usuario propietario.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-4">
            <Field label="Razón social" error={errors.legalName} required>
              {(c) => <Input {...c} {...form.register('legalName')} />}
            </Field>
            <div className="grid grid-cols-[11rem_1fr] gap-3">
              <Controller
                control={form.control}
                name="identificationType"
                render={({ field }) => (
                  <Field label="Tipo">
                    {(c) => (
                      <Select
                        {...c}
                        value={field.value}
                        onValueChange={field.onChange}
                        options={Object.entries(identificationTypeLabel).map(([value, label]) => ({
                          value,
                          label,
                        }))}
                      />
                    )}
                  </Field>
                )}
              />
              <Field label="Identificación" error={errors.identification} required>
                {(c) => <Input {...c} {...form.register('identification')} className="font-mono" />}
              </Field>
            </div>
            <Field label="Correo de la compañía" error={errors.email} required>
              {(c) => <Input {...c} {...form.register('email')} type="email" />}
            </Field>
            <Controller
              control={form.control}
              name="plan"
              render={({ field }) => (
                <Field
                  label="Plan"
                  hint={COMPANY_PLANS.find((p) => p.code === (field.value ?? 'full'))?.description}
                >
                  {(c) => (
                    <Select
                      {...c}
                      value={field.value ?? 'full'}
                      onValueChange={field.onChange}
                      options={COMPANY_PLANS.map((p) => ({ value: p.code, label: p.name }))}
                    />
                  )}
                </Field>
              )}
            />
            <Controller
              control={form.control}
              name="modules"
              render={({ field }) => (
                <div className="grid gap-2">
                  <div>
                    <div className="font-medium text-sm">Módulos opcionales</div>
                    <p className="text-muted-foreground text-xs">
                      General y Facturación vienen incluidos. Podés activar otros ahora o después.
                    </p>
                  </div>
                  <ModuleList compact value={field.value ?? []} onChange={field.onChange} />
                </div>
              )}
            />
            <div className="rounded-xl border border-border bg-subtle p-4">
              <div className="mb-3 font-medium text-sm">Propietario</div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Nombre" error={errors.ownerName} required>
                  {(c) => <Input {...c} {...form.register('ownerName')} />}
                </Field>
                <Field label="Correo" error={errors.ownerEmail} required>
                  {(c) => <Input {...c} {...form.register('ownerEmail')} type="email" />}
                </Field>
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={create.isPending}>
              Aprovisionar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const userCol = columnHelper<AdminUser>()

function UsersPanel({ search, onSearch }: PanelProps) {
  const queryClient = useQueryClient()
  const me = useSession((s) => s.user?.id)
  const [text, setText] = useSearchText(search, onSearch)
  const [open, setOpen] = useState(false)
  const [accessOf, setAccessOf] = useState<AdminUser | null>(null)
  const [secret, setSecret] = useState<{ email: string; password: string } | null>(null)
  const query = { search: search.q, page: search.page ?? 1, pageSize: 25 }
  const list = useQuery({
    queryKey: ['admin', 'users', query],
    queryFn: () => api.get<Paginated<AdminUser>>('/admin/users', { query }),
    placeholderData: keepPreviousData,
  })
  const update = useMutation({
    mutationFn: ({ id, ...body }: { id: string; status?: 'active' | 'disabled'; isSuperadmin?: boolean }) =>
      api.patch(`/admin/users/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
  const reset = useMutation({
    mutationFn: (u: AdminUser) =>
      api
        .post<{ temporaryPassword: string }>(`/admin/users/${u.id}/reset-password`)
        .then((r) => ({ ...r, email: u.email })),
    onSuccess: (r) => setSecret({ email: r.email, password: r.temporaryPassword }),
  })

  const columns = useMemo(
    () => [
      userCol.accessor('name', {
        header: 'Usuario',
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <Avatar name={row.original.name} />
            <div className="min-w-0">
              <div className="flex items-center gap-2 font-medium">
                {row.original.name}
                {row.original.isSuperadmin && (
                  <Badge variant="primary">
                    <ShieldIcon /> Super
                  </Badge>
                )}
              </div>
              <div className="truncate text-muted-foreground text-xs">{row.original.email}</div>
            </div>
          </div>
        ),
      }),
      userCol.accessor('companies', { header: 'Compañías', meta: { align: 'right' } }),
      userCol.accessor('lastLoginAt', {
        header: 'Último ingreso',
        meta: { hideOnMobile: true },
        cell: ({ getValue }) => (
          <span className="text-muted-foreground text-sm">
            {getValue() ? formatRelative(getValue()) : 'Nunca'}
          </span>
        ),
      }),
      userCol.accessor('createdAt', {
        header: 'Alta',
        meta: { hideOnMobile: true },
        cell: ({ getValue }) => (
          <span className="text-muted-foreground text-sm">{formatDate(getValue())}</span>
        ),
      }),
      userCol.accessor('status', {
        header: 'Estado',
        cell: ({ getValue }) =>
          getValue() === 'active' ? (
            <Badge variant="success" dot>
              Activo
            </Badge>
          ) : (
            <Badge dot>Deshabilitado</Badge>
          ),
      }),
      userCol.display({
        id: 'actions',
        header: '',
        meta: { align: 'right', className: 'w-24' },
        cell: ({ row }) => (
          <div className="flex justify-end gap-0.5">
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Compañías del usuario"
              title="Compañías del usuario"
              onClick={() => setAccessOf(row.original)}
            >
              <Building2Icon />
            </Button>
            {row.original.id === me ? (
              <span className="size-8" />
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon-sm" aria-label="Acciones">
                    <MoreHorizontalIcon />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => reset.mutate(row.original)}>
                    <KeyRoundIcon /> Restablecer contraseña
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() =>
                      update.mutate({ id: row.original.id, isSuperadmin: !row.original.isSuperadmin })
                    }
                  >
                    <ShieldIcon /> {row.original.isSuperadmin ? 'Quitar superadmin' : 'Hacer superadmin'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    destructive={row.original.status === 'active'}
                    onSelect={() =>
                      update.mutate({
                        id: row.original.id,
                        status: row.original.status === 'active' ? 'disabled' : 'active',
                      })
                    }
                  >
                    <PowerIcon /> {row.original.status === 'active' ? 'Deshabilitar' : 'Habilitar'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        ),
      }),
    ],
    [me, reset.mutate, update.mutate],
  )

  return (
    <>
      <DataTable
        columns={columns}
        data={list.data?.items}
        loading={list.isFetching}
        getRowId={(u) => u.id}
        isRowMuted={(u) => u.status !== 'active'}
        toolbar={
          <FilterBar
            actions={
              <Button onClick={() => setOpen(true)}>
                <PlusIcon /> Nuevo usuario
              </Button>
            }
          >
            <FilterSearch value={text} onChange={setText} placeholder="Buscar por nombre o correo…" />
            <ClearFilters active={!!text} onClear={() => setText('')} />
          </FilterBar>
        }
        pagination={
          list.data && {
            page: list.data.page,
            pageSize: list.data.pageSize,
            total: list.data.total,
            onPageChange: (p) => onSearch({ page: p }),
          }
        }
      />
      <CreateUserDialog
        open={open}
        onOpenChange={setOpen}
        onCreated={(email, password) => {
          queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
          setSecret({ email, password })
        }}
      />
      <UserAccessSheet user={accessOf} onClose={() => setAccessOf(null)} />
      <SecretDialog secret={secret} onClose={() => setSecret(null)} />
    </>
  )
}

function CreateUserDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  onCreated: (email: string, password: string) => void
}) {
  const form = useForm<AdminUserInput>({
    resolver: zodResolver(adminUserInput),
    defaultValues: { email: '', name: '', isSuperadmin: false },
  })
  const [access, setAccess] = useState<AccessValue>(EMPTY_ACCESS)
  useEffect(() => {
    if (open) {
      form.reset()
      setAccess(EMPTY_ACCESS)
    }
  }, [open, form])
  const create = useMutation({
    mutationFn: (v: AdminUserInput) =>
      api.post<{ user: AdminUser; temporaryPassword: string }>('/admin/users', {
        ...v,
        access: hasAccess(access) ? toAccessInput(access) : undefined,
      }),
    onSuccess: (r) => {
      onCreated(r.user.email, r.temporaryPassword)
      onOpenChange(false)
    },
  })
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <form onSubmit={form.handleSubmit((v) => create.mutate(v))} noValidate>
          <DialogHeader>
            <DialogTitle>Nuevo usuario</DialogTitle>
            <DialogDescription>
              Se crea con una contraseña temporal. Opcionalmente, dale acceso a compañías desde ya.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-4">
            <Field label="Nombre" error={form.formState.errors.name} required>
              {(c) => <Input {...c} {...form.register('name')} />}
            </Field>
            <Field label="Correo" error={form.formState.errors.email} required>
              {(c) => <Input {...c} {...form.register('email')} type="email" />}
            </Field>
            <Controller
              control={form.control}
              name="isSuperadmin"
              render={({ field }) => (
                <label className="flex items-center gap-2.5 text-sm">
                  <Switch checked={!!field.value} onCheckedChange={field.onChange} /> Superadministrador de la
                  plataforma
                </label>
              )}
            />
            <div className="rounded-xl border border-border bg-subtle p-4">
              <div className="mb-0.5 font-medium text-sm">Acceso a compañías</div>
              <p className="mb-3 text-muted-foreground text-xs">Opcional. También podés hacerlo después.</p>
              <AccessPicker value={access} onChange={setAccess} />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={create.isPending}>
              Crear
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function SecretDialog({
  secret,
  onClose,
}: {
  secret: { email: string; password: string } | null
  onClose: () => void
}) {
  const [copied, setCopied] = useState(false)
  return (
    <Dialog open={!!secret} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Contraseña temporal</DialogTitle>
          <DialogDescription>Compartila por un canal seguro; no se volverá a mostrar.</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="rounded-lg border border-border bg-subtle p-4">
            <div className="text-muted-foreground text-xs">Usuario</div>
            <div className="font-medium text-sm">{secret?.email}</div>
            <div className="mt-3 text-muted-foreground text-xs">Contraseña</div>
            <div className="font-mono text-base tracking-wide">{secret?.password}</div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={async () => {
              if (!secret) return
              const ok = await copyText(`${secret.email}\n${secret.password}`, {
                label: 'Credenciales de acceso',
                successMessage: false,
              })
              if (!ok) return
              setCopied(true)
              setTimeout(() => setCopied(false), 1500)
            }}
          >
            {copied ? <CheckIcon /> : <CopyIcon />} {copied ? 'Copiado' : 'Copiar'}
          </Button>
          <Button onClick={onClose}>Listo</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
